import { css } from 'styled-components';

const mediaQuery = {
  legacyMobile: '@media only screen and (max-width: 359.99px)',
  mobile: '@media only screen and (min-width: 360px)',
  portrait: '@media only screen and (min-width: 600px)',
  landscape: '@media only screen and (min-width: 992px)',
  wide: '@media only screen and (min-width: 1200px)',
  superWide: '@media only screen and (min-width: 1600px)'
};
const borderRadius = {
  /**
   * 0px === 0rem
   */
  square: '0rem',
  /**
   * 4px === 0.25rem
   */
  sharp: '0.25rem',
  /**
   * 8px === 0.5rem
   */
  rounded: '0.5rem',
  /**
   * 16px === 1rem
   */
  superRounded: '1rem',
  /**
   * 50%
   */
  pill: '50%'
};
const fontFamily = {
  default: 'SkattaSansUI, "Segoe UI", Arial, "Helvetica Neue", sans-serif;'
};
const fontWeight = {
  black: '900',
  bold: '700',
  medium: '500',
  regular: '400'
};
const shadows = {
  card: 'box-shadow: 0 5px 10px 0 rgba(136, 136, 136, 0.2);',
  cardLight: 'box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);',
  cardLightDouble: 'box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 1px 3px 0px rgba(0, 0, 0, 0.1);',
  iconBall: 'box-shadow: -1px -1px 4px 0 rgba(0, 0, 0, 0.05), 1px 1px 4px 0 rgba(0, 0, 0, 0.05);',
  listItem: 'box-shadow: inset 0 -1px 0 0 rgba(0, 0, 0, 0.1);',
  navigation: 'box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.05);',
  navigationReversed: 'box-shadow: 0 -1px 4px 0 rgba(0, 0, 0, 0.05);',
  popover: 'box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.05), 0px 4px 6px -1px rgba(0, 0, 0, 0.1);',
  profileSections: 'box-shadow: 0 4px 8px 0 rgba(136, 136, 136, 0.1);',
  rulesAcceptContainer: 'box-shadow: 0px -20px 20px 0px rgba(125,125,125,0.1);',
  sideNavigation: 'box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.05), 0 4px 6px -1px rgba(0, 0, 0, 0.1);',
  toggleSwitchButton: 'box-shadow: 0 4px 2px 0 rgba(0, 0, 0, 0.1);'
};

/**
 * Colors for the "light" theme.
 */
const color = {
  text: {
    primary: '#282828',
    secondary: '#3E3E3E',
    tertiary: '#565656',
    quarternary: '#757575',
    quinary: '#888888',
    kOrange: '#CB4700',
    inverted: '#FFFFFF'
  },
  /**
   * Background colors.
   */
  bg: {
    primary: '#FFFFFF',
    secondary: '#FAFAFA',
    tertiary: '#F6F6F6',
    inverted: '#2B2B2B',
    hover: '#FCF0E1',
    error: '#FDE6E8',
    success: '#EAF3E6',
    warning: '#F7F4E6',
    notification: '#E6F1FA'
  },
  fill: {
    primary: '#F0F0F0',
    primaryElevated: '#F0F0F0',
    secondary: '#DDDDDD',
    tertiary: '#C0C0C0'
  },
  /**
   * Kesko brand colors.
   */
  kBrand: {
    kOrange: '#F86800',
    kOrangeInverted: '#FD8726',
    pureBlack: '#000000',
    pureWhite: '#FFFFFF'
  },
  /**
   * Grocery trade brand colors.
   */
  gtBrand: {
    kcmRed: '#C20614',
    kcmBrightRed: '#E20000',
    kcmWarmRed: '#800000',
    kcmDarkRed: '#570000',
    ksmBlue: '#002855',
    ksmSecondaryBlue: '#335377',
    kmBerry: '#9B2242',
    kmBurgundy: '#651D32',
    nkBlue: '#003C96',
    nkGreen: '#66DD00',
    nkLightBlue: '#B0D2EB'
  },
  status: {
    blue: '#0076CA',
    green: '#2D8300',
    red: '#E80014',
    yellow: '#B08C00'
  }
};
const surface = {
  elevated: css(["box-shadow:0 1px 2px rgba(0,0,0,0.04),0 1px 4px rgba(0,0,0,0.12);"]),
  floatLow: css(["box-shadow:0 8px 24px 2px rgba(0,0,0,0.04),0 2px 28px rgba(0,0,0,0.02),0 4px 4px rgba(0,0,0,0.08);"]),
  floatMedium: css(["box-shadow:0 20px 32px 2px rgba(0,0,0,0.08),0 4px 36px rgba(0,0,0,0.04),0 8px 12px rgba(0,0,0,0.12);"]),
  floatHigh: css(["box-shadow:0 24px 36px 4px rgba(0,0,0,0.12),0 8px 44px rgba(0,0,0,0.08),0 12px 16px rgba(0,0,0,0.16);"])
};
const spacing = {
  /**
   * 4px === 0.25rem
   */
  xxs: '0.25rem',
  /**
   * 8px === 0.5rem
   */
  xs: '0.5rem',
  /**
   * 16px === 1rem
   */
  sm: '1rem',
  /**
   * 24px === 1.5rem
   */
  md: '1.5rem',
  /**
   * 32px === 2rem
   */
  lg: '2rem',
  /**
   * 40px === 2.5rem
   */
  xl: '2.5rem',
  /**
   * 48px === 3rem
   */
  xxl: '3rem',
  /**
   * 64px === 4rem
   */
  xxxl: '4rem'
};
const fontSize = {
  title: {
    /**
     * 40px === 2.5rem
     */
    0: '2.5rem',
    /**
     * 32px === 2rem
     */
    1: '2rem',
    /**
     * 24px === 1.5rem
     */
    2: '1.5rem',
    /**
     * 20px === 1.25rem
     */
    3: '1.25rem',
    /**
     * 18px === 1.125rem
     */
    4: '1.125rem',
    /**
     * 12px === 0.75rem
     */
    5: '0.75rem'
  },
  text: {
    /**
     * 19px === 1.1875rem
     */
    l: '1.1875rem',
    /**
     * 16px === 1rem
     */
    m: '1rem',
    /**
     * 14px === 0.875rem
     */
    s: '0.875rem',
    /**
     * 12px === 0.75rem
     */
    xs: '0.75rem'
  }
};
const lineHeight = {
  title: {
    /**
     * 46px === 2.875rem
     */
    0: '2.875rem',
    /**
     * 40px === 2.5rem
     */
    1: '2.5rem',
    /**
     * 32px === 2rem
     */
    2: '2rem',
    /**
     * 28px === 1.75rem
     */
    3: '1.75rem',
    /**
     * 26px === 1.625rem
     */
    4: '1.625rem',
    /**
     * 24px === 1.5rem
     */
    5: '1.25rem'
  },
  text: {
    /**
     * 24px === 1.5rem
     */
    l: '1.5rem',
    /**
     * 22px === 1.375rem
     */
    m: '1.375rem',
    /**
     * 20px === 1.25rem
     */
    s: '1.25rem',
    /**
     * 16px === 1rem
     */
    xs: '1rem'
  }
};
const zIndices = {
  base: 1,
  onTop: 2,
  infoBox: 9000
};
const Theme = {
  k: {
    color,
    shadows,
    fontSize,
    fontFamily,
    fontWeight,
    surface,
    spacing,
    lineHeight,
    mediaQuery,
    borderRadius,
    zIndices
  }
};

export { Theme, borderRadius, fontWeight, mediaQuery };
